const DRAG_THRESHOLD = 6;
const INTERACTIVE_SELECTOR = 'button, input, select, textarea, summary, [contenteditable="true"]';

class CustomMediaScrollSnap extends HTMLElement {
  #track = null;
  #abortController = null;
  #activePointerId = null;
  #startX = 0;
  #previousX = 0;
  #isDragging = false;
  #suppressClick = false;
  #direction = 1;

  connectedCallback() {
    if (this.#abortController) return;

    this.#track = this.querySelector('[data-custom-media-scroll-snap-track]');
    if (!this.#track) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#track.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.#track.addEventListener('lostpointercapture', this.#handlePointerEnd, { signal });
    this.#track.addEventListener('click', this.#handleClick, { capture: true, signal });
    window.addEventListener('pointermove', this.#handlePointerMove, { signal });
    window.addEventListener('pointerup', this.#handlePointerEnd, { signal });
    window.addEventListener('pointercancel', this.#handlePointerEnd, { signal });
    window.addEventListener('blur', this.#handleWindowBlur, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resetDragState();
    this.#track = null;
  }

  #handlePointerDown = (event) => {
    if (!this.#track || event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary) return;
    if (!(event.target instanceof Element) || event.target.closest(INTERACTIVE_SELECTOR)) return;
    if (this.#track.scrollWidth <= this.#track.clientWidth) return;

    this.#activePointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#previousX = event.clientX;
    this.#isDragging = false;
    this.#suppressClick = false;
    this.#direction = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
  };

  #handlePointerMove = (event) => {
    if (!this.#track || event.pointerId !== this.#activePointerId) return;

    const totalDistance = event.clientX - this.#startX;
    if (!this.#isDragging && Math.abs(totalDistance) < DRAG_THRESHOLD) return;

    if (!this.#isDragging) {
      this.#isDragging = true;
      this.#suppressClick = true;
      this.#track.classList.add('is-dragging');
      this.#track.setPointerCapture(event.pointerId);
    }

    event.preventDefault();

    const delta = this.#previousX - event.clientX;
    this.#previousX = event.clientX;
    this.#track.scrollLeft += delta * this.#direction;
  };

  #handlePointerEnd = (event) => {
    if (event.pointerId !== this.#activePointerId) return;

    const pointerId = this.#activePointerId;
    const wasDragging = this.#isDragging;

    this.#activePointerId = null;
    this.#isDragging = false;
    this.#track?.classList.remove('is-dragging');

    if (this.#track?.hasPointerCapture(pointerId)) {
      this.#track.releasePointerCapture(pointerId);
    }

    if (!wasDragging) {
      this.#suppressClick = false;
      return;
    }

    // The synthetic click follows pointerup in the same task. Clear the guard
    // afterwards as a fallback for browsers that omit the click after a drag.
    window.setTimeout(() => {
      this.#suppressClick = false;
    });
  };

  #handleClick = (event) => {
    if (!this.#suppressClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#suppressClick = false;
  };

  #handleWindowBlur = () => {
    this.#resetDragState();
  };

  #resetDragState() {
    const pointerId = this.#activePointerId;

    this.#activePointerId = null;
    this.#isDragging = false;
    this.#suppressClick = false;
    this.#track?.classList.remove('is-dragging');

    if (pointerId !== null && this.#track?.hasPointerCapture(pointerId)) {
      this.#track.releasePointerCapture(pointerId);
    }
  }
}

if (!customElements.get('custom-media-scroll-snap')) {
  customElements.define('custom-media-scroll-snap', CustomMediaScrollSnap);
}
